import { useState, useEffect } from "react";

interface TypingAnimationResult {
  text: string;
  isFadingIn: boolean;
}

export function useTypingAnimation(prompts: string[], typingSpeed = 50, pauseDuration = 3000, initialDelay = 8000): TypingAnimationResult {
  const [displayText, setDisplayText] = useState(prompts[0]);
  const [currentPromptIndex, setCurrentPromptIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [charIndex, setCharIndex] = useState(0);
  const [showCursor, setShowCursor] = useState(false);
  const [isShowingBase, setIsShowingBase] = useState(true);
  const [isFadingIn, setIsFadingIn] = useState(false);

  useEffect(() => {
    // Show base prompt without animation
    if (isShowingBase) {
      const timeout = setTimeout(() => {
        setIsShowingBase(false);
        setIsFadingIn(false);
        setDisplayText("");
        setCurrentPromptIndex(1);
        setIsTyping(true);
        setShowCursor(true);
      }, initialDelay);
      return () => clearTimeout(timeout);
    }

    const currentPrompt = prompts[currentPromptIndex];

    if (isTyping) {
      if (charIndex < currentPrompt.length) {
        const timeout = setTimeout(() => {
          setDisplayText(currentPrompt.slice(0, charIndex + 1));
          setCharIndex(charIndex + 1);
        }, typingSpeed);
        return () => clearTimeout(timeout);
      } else {
        // Finished typing, pause before next prompt
        const timeout = setTimeout(() => {
          setIsTyping(false);
          setCharIndex(0);
        }, pauseDuration);
        return () => clearTimeout(timeout);
      }
    } else {
      // Move to next prompt or back to base
      const timeout = setTimeout(() => {
        const nextIndex = currentPromptIndex + 1;
        if (nextIndex >= prompts.length) {
          // Cycle complete - show base prompt again with fade
          setDisplayText(prompts[0]);
          setShowCursor(false);
          setIsFadingIn(true);
          setIsShowingBase(true);
          setCurrentPromptIndex(0);
        } else {
          setDisplayText("");
          setCurrentPromptIndex(nextIndex);
          setIsTyping(true);
        }
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [charIndex, currentPromptIndex, isTyping, prompts, typingSpeed, pauseDuration, isShowingBase, initialDelay]);

  // Cursor blink effect - only when not showing base
  useEffect(() => {
    if (isShowingBase) return;
    const interval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 530);
    return () => clearInterval(interval);
  }, [isShowingBase]);

  return {
    text: displayText + (showCursor && !isShowingBase ? "|" : ""),
    isFadingIn
  };
}
